"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { IServiceData } from "@/types/strapi";
import ServiceItem from "../_molecules/ServiceItem";
import { getImageUrl } from "@/utils/images";
import RichText from "../_atoms/richtext";

export default function StickyServiceSection({
	heading,
	text,
	items,
	textCenter = false,
}: {
	heading: string;
	text: string;
	items: IServiceData[];
	textCenter: boolean;
}) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [activeImage, setActiveImage] = useState<string>(
		items?.[0]?.Header?.img
			? getImageUrl(
					items[0].Header.img.formats?.medium?.url ??
						items[0].Header.img.url,
				)
			: "/placeholder.png",
	);
	const itemsRef = useRef<(HTMLLIElement | null)[]>([]);

	useEffect(() => {
		const handleScroll = () => {
			const middle = window.innerHeight / 2;
			let closestIndex = 0;
			let closestDistance = Infinity;

			itemsRef.current.forEach((el, i) => {
				if (!el) return;
				const rect = el.getBoundingClientRect();
				const elCenter = rect.top + rect.height / 2;
				const distance = Math.abs(middle - elCenter);
				if (distance < closestDistance) {
					closestDistance = distance;
					closestIndex = i;
				}
			});

			if (closestIndex !== activeIndex) {
				setActiveIndex(closestIndex);

				const headerImg =
					items[closestIndex]?.Header?.img?.formats?.medium?.url ??
					items[closestIndex]?.Header?.img?.url;
				const url = headerImg
					? getImageUrl(headerImg)
					: "/placeholder.png";
				setActiveImage(url);
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll(); // init
		return () => window.removeEventListener("scroll", handleScroll);
	}, [items, activeIndex]);

	return (
		<section className="container relative pb-20 md:pb-48">
			<h2 className={`${textCenter ? "text-center" : "text-left"}`}>
				{heading}
			</h2>
			<RichText content={text} className="md:max-w-3xl mx-auto mt-5" />
			<div className="md:flex justify-between relative top-10 bottom-0">
				<ul className="md:w-[50%]">
					{items.map((item, i) => {
						return (
							<li
								key={item.id}
								ref={(el) => {
									itemsRef.current[i] = el;
								}}
								className={`mt-10 md:first:mt-40 md:my-48 md:last:mb-26
 bg-cover bg-center rounded-lg transition-transform duration-300 md:!bg-none`}
							>
								<div className="relative w-full h-36 md:hidden">
									<Image
										src={getImageUrl(
											item.Header?.img?.formats?.small
												?.url ?? item.Header?.img?.url,
										)}
										alt={
											item.Header?.img?.alternativeText ??
											"illustration du service"
										}
										fill
										sizes="(max-width: 768px) 100vw, 0px"
										quality={75}
										className="object-cover w-full h-full transition-opacity duration-500 rounded-lg "
									/>
								</div>
								<ServiceItem
									item={item}
									className="p-6 py-10"
								/>
							</li>
						);
					})}
				</ul>
				<div className="hidden md:block md:w-[30%] h-[50vh] sticky top-[25vh] bottom-[25vh] mt-16">
					<div className="relative h-full w-full">
						<Image
							src={activeImage}
							alt="illustration du service"
							fill
							sizes="(max-width: 768px) 0px, 30vw"
							quality={75}
							className="object-cover w-full h-full transition-opacity duration-500 rounded-lg"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
